/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiClient } from "./api";
import { BackendResponse } from "../pages/activity/TherapySetVsDelivered";

export interface ClearTherapyVitalStats {
  min: number;
  max: number;
  median: number;
}

export interface ClearTherapySessionSummary {
  sessionCount: number;
  totalDuration: number;
  totalPepTime: number;
  totalOscTime: number;
  totalNebTime: number;
  totalPepWithNebTime: number;
  totalOscWithNebTime: number;
  totalPhases: number;
  modes: string[];
  nebConnected: boolean;
}

export interface ClearTherapyDataPoint {
  date: string;
  avgNebDuration: number;
  avgPepDuration: number;
  avgOscDuration: number;
  avgPepWithNebTime?: number;
  avgOscWithNebTime?: number;
  pepPressure: ClearTherapyVitalStats | null;
  oscPressure: ClearTherapyVitalStats | null;
  summary?: ClearTherapySessionSummary | null;
}

export interface ClearTherapyVitalsPoint {
  date: string;
  spo2: ClearTherapyVitalStats | null;
  aqi: ClearTherapyVitalStats | null;
  heartRate: ClearTherapyVitalStats | null;
}

export interface ClearTherapySessionDetail {
  date: string;
  refId: string;
  startTime: string | null;
  endTime: string | null;
  mode: string | null;
  nebConnected: boolean;
  totalDuration: number;
  totalPhases: number;
  nebDuration: number;
  pepDuration: number;
  oscDuration: number;
  pepPressure: ClearTherapyVitalStats | null;
  oscPressure: ClearTherapyVitalStats | null;
  pepWithNebTime: number;
  oscWithNebTime: number;
}

export interface ClearTherapySummaryResponse {
  therapyData: ClearTherapyDataPoint[];
  vitalsData: ClearTherapyVitalsPoint[];
  sessions?: ClearTherapySessionDetail[];
}

export const getClearTherapySummary = async (
  profileId: number,
  startDate: string,
  endDate: string
): Promise<ClearTherapySummaryResponse> => {
  try {
    const response = await apiClient.get("/api/clear-therapy-summary", {
      params: { profileId, startDate, endDate },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching clear therapy summary:", error);
    throw error;
  }
};

// Create api service to fetch pressure flow graph summary by date range
// POST /api/pressure-flow-graph"
export const getPressureFlowGraphSummaryByDateRange = async (
  profileId: number,
  startDate: string,
  endDate: string
): Promise<BackendResponse> => {
  try {
    // startDate/endDate are already "YYYY-MM-DD" — append time directly.
    // Never use new Date("YYYY-MM-DD"): JS parses it as UTC midnight, which
    // shifts the local date back by a day in US timezones (UTC-), causing
    // Day view to query the wrong day entirely → 0 cycles returned.
    const startDateTime = `${startDate}T00:00:00.000`;
    const endDateTime = `${endDate}T23:59:59.999`;
    const response = await apiClient.post("/api/pressure-flow-graph", {
      profileId,
      startDate: startDateTime,
      endDate: endDateTime,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching pressure flow graph summary:", error);
    throw error;
  }
};
